import  { useEffect, useState } from "react";
import "./SeventhReport.css";

const QTag = ({ q, a, impacts }) => (
  <div style={{display:'flex',flexWrap:'wrap',gap:'5px 10px',alignItems:'center',background:'#f0f4ff',border:'1px solid #c7d7fa',borderRadius:'6px',padding:'7px 12px',marginBottom:'12px',fontSize:'12px',lineHeight:1.5}}>
    <span style={{background:'#3b5bdb',color:'#fff',fontWeight:700,padding:'2px 8px',borderRadius:4,fontSize:'10px',whiteSpace:'nowrap'}}>📋 FORM QUESTION</span>
    <span style={{color:'#1e3a8a',fontStyle:'italic'}}>"{q}"</span>
    <span style={{color:'#6b7280',fontSize:'11px'}}>→</span>
    <span style={{color:'#047857',fontWeight:700}}>You answered: <strong>{a}</strong></span>
    <span style={{color:'#6b7280',fontSize:'11px'}}>→</span>
    <span style={{color:'#7c3aed',fontSize:'11px'}}>📊 Impacts: {impacts}</span>
  </div>
);

const SeventhReport = ({ res }) => {
  const [bestFitChannel, setBestFitChannel] = useState("");
  const [pricingCurrency, setPricingCurrency] = useState("");
  const [economicValue, setEconomicValue] = useState("");
  const [ecommerceValue, setEcommerceValue] = useState("");

  // Helper → case-insensitive check
  const isYes = (val, expected) =>
    val?.toString().trim().toLowerCase() === expected.toLowerCase();

  // Helper → remove leading symbols when displaying
  const clean = (str) =>
    str ? str.replace(/^[%^*&()]/, "").trim() : "";

  useEffect(() => {
    if (res.bestFitChannel) setBestFitChannel(res.bestFitChannel);
    if (res.pricingCurrency) setPricingCurrency(res.pricingCurrency);
    if (res.economic) setEconomicValue(res.economic);
    if (res.ecommerce) setEcommerceValue(res.ecommerce);
  }, [res]);

  // 🟦 6A: Best Fit Channel
  const channelData = {
    "Offline/Retail": {
      rank: "1",
      investor: "Slower scale but local trust",
      useCase: "Hardware, AgriTech, FMCG",
    },
    "Community/Referrals": {
      rank: "2",
      investor: "High retention, low CAC",
      useCase: "Consumer apps, EdTech, HealthTech",
    },
    "social media": {
      rank: "🥉 3",
      investor: "Fast traction, great for MVP stage",
      useCase: "B2C, D2C, C2C",
    },
    "B2B": {
      rank: "4 & 5",
      investor: "Strong defensibility, high contract value",
      useCase: "SaaS, Enterprise Tech",
    },
    "Partnerships": {
      rank: "4 & 5",
      investor: "Strong defensibility, high contract value",
      useCase: "EdTech, HealthTech, SaaS",
    },
    "App Stores": {
      rank: "6",
      investor: "Global reach, recurring users",
      useCase: "SaaS, EdTech, FinTech",
    },
    "Govt/Institutional": {
      rank: "7",
      investor: "Stable but slow cycles",
      useCase: "HealthTech, AgriTech, Impact sectors",
    },
  };

  const selectedChannelData = channelData[bestFitChannel];

  // 🟩 6B - Pricing Currency
  const currencyContent =
    pricingCurrency?.toLowerCase() === "in dollars"
      ? "Use dollars for global products/services, premium users, or to avoid local currency depreciation."
      : pricingCurrency?.toLowerCase() === "local currency"
      ? "Use local currency for stronger local appeal, affordability, and when costs are mainly domestic."
      : "";

  // 🟨 6C - Economic Capacity
  const economicContent = isYes(economicValue, "(Yes")
    ? "If pricing matches economic capacity, it ensures wider affordability, strong adoption, and investor confidence."
    : "If not matched, we will apply tiered pricing, subscriptions, and cost optimization to maintain accessibility and profitability.";

  // 🟧 6D - User Accessibility
  const ecommerceContent = isYes(ecommerceValue, ")Yes")
    ? "If users can easily obtain and use our product or service, it leads to higher satisfaction, faster growth, and stronger investor confidence due to clear accessibility and usability."
    : "However, if users face difficulty, you will simplify your product or service, improve logistics and payment options, and enhance user support to ensure smooth access and experience. This adaptability will strengthen our market presence and customer trust.";

  return (
    <>
    <QTag
      q="What is your best-fit distribution channel? What currency will you price in, and does your pricing match your customers' economic capacity?"
      a={`Channel: ${bestFitChannel || "—"} · Currency: ${pricingCurrency || "—"} · Economic fit: ${isYes(economicValue, "(Yes") ? "Yes" : "No"} · User accessibility: ${isYes(ecommerceValue, ")Yes") ? "Yes" : "No"}`}
      impacts="GTM strategy effectiveness, distribution efficiency, pricing attractiveness, customer accessibility score"
    />
    <div style={{ padding: "22px" }}>
      <div className="seventh-module-container">
        {/* Corner Borders */}
        <span className="corner top-left"></span>
        <span className="corner top-right"></span>
        <span className="corner bottom-left"></span>
        <span className="corner bottom-right"></span>

        <h3 className="module-title"> <span className="module-prefix">Module 6:</span>  Go-to-Market Check</h3>

        {/* 6A - Best Fit Channel */}
        {selectedChannelData && (
          <table className="seventh-module-table table-6a">

            <thead>
              <tr>
                <th>S.No</th>
                <th>Rank</th>
                <th>Channel</th>
                <th>Investor View</th>
                <th>Ideal Use Case</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="serial-cell">6 A</td>
                <td>{selectedChannelData.rank}</td>
                <td>{clean(bestFitChannel)}</td>
                <td>{selectedChannelData.investor}</td>
                <td>{selectedChannelData.useCase}</td>
              </tr>
            </tbody>
          </table>
        )}

        {/* 6B */}
        {pricingCurrency && (
          <table className="seventh-module-table">
            <tbody>
              <tr>
                <td className="serial-cell">6 B</td>
                <td>{clean(pricingCurrency)}</td>
                <td>{currencyContent}</td>
              </tr>
            </tbody>
          </table>
        )}

        {/* 6C */}
        {economicValue && (
          <table className="seventh-module-table">
            <tbody>
              <tr>
                <td className="serial-cell">6 C</td>
                <td>{isYes(economicValue, "(Yes") ? "Yes" : "No"}</td>
                <td>{economicContent}</td>
              </tr>
            </tbody>
          </table>
        )}

        {/* 6D */}
        {ecommerceValue && (
          <table className="seventh-module-table">
            <tbody>
              <tr>
                <td className="serial-cell">6 D</td>
                <td>{isYes(ecommerceValue, ")Yes") ? "Yes" : "No"}</td>
                <td>{ecommerceContent}</td>
              </tr>
            </tbody>
          </table>
        )}
      </div>
    </div>
    </>
  );
};

export default SeventhReport;
